import { useNavigate } from 'react-router-dom'
import {
  Users,
  Calendar,
  BarChart3,
  Inbox,
  Library,
  Shield,
  ShoppingBag,
  Search,
  SquarePen,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { ThemeToggle } from '@/components/theme-toggle'
import { AccountMenu } from '@/components/layout/AccountMenu'
import { AgentList } from '@/components/layout/AgentList'
import { PageList } from '@/components/layout/PageList'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { useUiStore, type NavSegment } from '@/stores/useUiStore'
import { useAuthStore, selectUser } from '@/stores/useAuthStore'
import { useConnectedAccountsStore } from '@/stores/useConnectedAccountsStore'
import { usePendingApprovalsCount } from '@/hooks/usePendingApprovalsCount'
import { useIsMobile } from '@/hooks/useMediaQuery'
import { CHAT_THREAD_PATH } from '@/hooks/useIsChatHome'

export interface NavItem {
  label: string
  href: string
  icon: React.ElementType
  badgeCount?: number
  /** Other path prefixes that belong to this page (Content lives under Library). */
  alsoActiveOn?: string[]
}

// The daily pages only, always visible: no per-business feature gating hides any of
// these. Content and Campaigns fold into Library and Results; Reviews is reached from
// Inbox; Settings opens from the owner's name at the bottom.
export const PAGE_NAV_ITEMS: NavItem[] = [
  { label: 'Approvals', href: '/approvals', icon: Shield },
  { label: 'Inbox', href: '/inbox', icon: Inbox },
  { label: 'Calendar', href: '/calendar', icon: Calendar },
  { label: 'Leads', href: '/leads', icon: Users },
  { label: 'Results', href: '/results', icon: BarChart3, alsoActiveOn: ['/campaigns'] },
  {
    label: 'Library',
    href: '/library',
    icon: Library,
    alsoActiveOn: ['/content', '/media', '/files'],
  },
]

const shopifyNavItem: NavItem = { label: 'Shopify', href: '/shopify', icon: ShoppingBag }
const adminNavItem: NavItem = { label: 'Admin', href: '/admin', icon: Shield }

// Nav items rendered from live data rather than unconditionally.
const CONDITIONAL_NAV_HREFS = ['/shopify', '/admin'] as const
type ConditionalNavHref = (typeof CONDITIONAL_NAV_HREFS)[number]

const isConditionalNavHref = (href: string): href is ConditionalNavHref =>
  (CONDITIONAL_NAV_HREFS as readonly string[]).includes(href)

interface SidebarProps {
  onSearch: () => void
  /** `desktop` is the 288px side panel; `home` fills the phone's home screen. */
  variant?: 'desktop' | 'home'
}

export function Sidebar({ onSearch, variant = 'desktop' }: SidebarProps) {
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const navSegment = useUiStore((state) => state.navSegment)
  const setNavSegment = useUiStore((state) => state.setNavSegment)
  const user = useAuthStore(selectUser)
  const isAdmin = !!(user as Record<string, unknown> | null)?.is_admin
  const pendingApprovalsCount = usePendingApprovalsCount()
  const accounts = useConnectedAccountsStore((s) => s.accounts)
  const shopifyConnected = accounts.some(
    (a) => a.platform === 'shopify' && a.status === 'connected'
  )
  // Typed over the declaration, so adding an href to CONDITIONAL_NAV_HREFS
  // without a visibility rule here is a compile error.
  const conditionalNavVisible: Record<ConditionalNavHref, boolean> = {
    '/shopify': shopifyConnected,
    '/admin': isAdmin,
  }

  const extraItems: NavItem[] = [shopifyNavItem, adminNavItem].filter(
    (item) => !isConditionalNavHref(item.href) || conditionalNavVisible[item.href]
  )

  const pageItems = PAGE_NAV_ITEMS.map((item) =>
    item.href === '/approvals' ? { ...item, badgeCount: pendingApprovalsCount } : item
  )

  const handleSegment = (segment: NavSegment) => {
    setNavSegment(segment)
    // On desktop Agents means the Marketing thread; on a phone it only swaps the list.
    if (segment === 'agents' && !isMobile) navigate(CHAT_THREAD_PATH)
  }

  return (
    <aside
      data-testid="sidebar"
      className={cn(
        'bg-sidebar flex h-full shrink-0 flex-col gap-3 px-3 py-3.5',
        variant === 'desktop' ? 'border-sidebar-border w-72 border-r' : 'w-full'
      )}
    >
      <div className="flex items-center gap-1">
        <SegmentedControl
          aria-label="Agents or pages"
          value={navSegment}
          onValueChange={handleSegment}
          className="min-w-0 flex-1"
          options={[
            { value: 'agents', label: 'Agents' },
            { value: 'pages', label: 'Pages' },
          ]}
        />
        <button
          type="button"
          onClick={() => navigate(CHAT_THREAD_PATH)}
          aria-label="New chat"
          data-testid="sidebar-new-chat"
          className="text-muted-foreground hover:bg-sidebar-accent/60 flex size-11 shrink-0 items-center justify-center rounded-lg md:size-9"
        >
          <SquarePen className="size-[18px]" />
        </button>
      </div>

      <button
        type="button"
        onClick={onSearch}
        data-testid="search-button"
        className="bg-field text-muted-foreground flex h-11 items-center gap-2 rounded-[10px] px-3 text-left text-sm md:h-10"
      >
        <Search className="size-4 shrink-0" />
        <span className="flex-1">Search</span>
        <kbd className="font-mono text-[0.625rem]">⌘K</kbd>
      </button>

      {navSegment === 'agents' ? <AgentList /> : <PageList items={[...pageItems, ...extraItems]} />}

      <div className="border-sidebar-border flex items-center gap-1 border-t pt-1">
        <div className="min-w-0 flex-1">
          <AccountMenu variant="row" />
        </div>
        <div className="md:hidden">
          <ThemeToggle />
        </div>
      </div>
    </aside>
  )
}
